import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import {
  isCanceledUploadFile,
  isUploadedFile,
  isUploadFailedFile,
} from "@custom-types/file-upload-type-guards";
import {
  FileUploadTask,
  InvalidUploadToStore,
  MultiFileUploadResponse,
  MultiUploadedFileResponse,
  UploadedFile,
  UploadFileParams,
  UploadMultipleFilesParams,
  ValidateFile,
} from "@custom-types/file-upload-types";
import { GUID } from "@faro-lotv/foundation";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { doUpload, validateFile } from "@hooks/file-upload-utils";
import { useToast } from "@flat-ui";
import { DateTimeUtils } from "@stellar/utils";
import { useAppDispatch } from "@store/store-helper";
import { setOne } from "@store/upload-tasks/upload-tasks-slice";
import { checkMagicFileHeader } from "@utils/file-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { getRealFile, isZipFile } from "@utils/zip-file";
import { uniqueId } from "es-toolkit/compat";

interface UseFileUpload {
  /**
   * Uploads a single file
   *
   * @returns boolean Returns either true or false, depending on whether the file uploaded successfully or not.
   */
  uploadSingleFile: (params: UploadFileParams) => Promise<boolean>;

  /**
   * Uploads a big file with chunks
   *
   * @returns boolean Returns either true or false, depending on whether the file uploaded successfully or not.
   */
  uploadFileWithChunks: (params: UploadFileParams) => Promise<boolean>;

  /**
   * Upload multiple files
   *
   * @returns boolean Returns either true when all promises finished
   */
  uploadMultipleFiles: (params: UploadMultipleFilesParams) => Promise<boolean>;

  /**
   * Validates a file and adds error to the upload task
   *
   * @returns boolean True if the file is valid to upload
   */
  validateAndAddFailedTask: (params: ValidateFile) => boolean;
}

/** get corresponding bucket so we can chart easily the Upload speed distribution (speed buckets e.g. for current month) */
function getUploadSpeedBucket(
  uploadSpeedMBps: number
): MultiUploadedFileResponse["uploadSpeedBucketMBps"] {
  if (uploadSpeedMBps <= 2) {
    return "0-2";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 5) {
    return "2-5";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 10) {
    return "5-10";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 20) {
    return "10-20";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 50) {
    return "20-50";
  }
  return ">50";
}

/** A hook to upload a file */
export function useFileUpload(): UseFileUpload {
  const { handleErrorWithToast } = useErrorContext();
  const { uploadManager } = useFileUploadContext();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();

  async function uploadSingleFile({
    file,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    context,
    isSilentError = false,
  }: UploadFileParams): Promise<boolean> {
    if (!file.type) {
      // file.type is used to determine the the content-type header of the upload request
      // and must be set for some storage providers.
      openToast({
        title: "File type Missing",
        message: "The selected file is not valid type to upload",
        variant: "error",
      });
      return false;
    }

    try {
      await checkMagicFileHeader(file);

      onUploadStart();

      // Fetch upload request details
      const uploadRequest =
        await faroCoreApiClient.file.generateSignedUploadUrlWithHeaders({
          contentType: file.type,
          companyId: context.companyId ?? "",
          projectId: context.projectId ?? "",
          memberId: context.userId ?? "",
        });

      // Make sure, FileExt and ZipFile are uploaded correctly
      let blob: ArrayBuffer | File;
      if (isZipFile(file)) {
        blob = await file.arrayBuffer();
      } else {
        blob = getRealFile(file);
      }

      // Upload the file
      const result = await faroCoreApiClient.file.uploadAsset({
        method: uploadRequest.method,
        uploadUrl: uploadRequest.uploadUrl,
        file: blob,
        type: file.type,
        headers: uploadRequest.headers,
        onUploadProgress,
      });
      if (!result.isUploaded) {
        throw new Error(
          `Failed to upload <var>${file.name}</var>: ${result.status} ${result.statusText}`
        );
      }

      const downloadUrl = uploadRequest.uploadUrl.split("?")[0];

      // Callback with the new file url and file
      onUploadComplete(downloadUrl, context);

      return true;
    } catch (error) {
      const handleError = isSilentError
        ? sentryCaptureError
        : handleErrorWithToast;
      handleError({
        id: `uploadSingleFile-${Date.now().toString()}`,
        title: "Error uploading file",
        error,
      });
      return false;
    }
  }

  async function uploadFileWithChunks({
    file,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    context,
    isSilentError = false,
  }: UploadFileParams): Promise<boolean> {
    if (!file.type) {
      // file.type is used to determine the the content-type header of the upload request
      // and must be set for some storage providers.
      openToast({
        title: "File type Missing",
        message: "The selected file is not valid type to upload",
        variant: "error",
      });
      return false;
    }

    try {
      await checkMagicFileHeader(file);

      onUploadStart();

      const uploadResult = await doUpload({
        file,
        // The CoreAPI endpoint to upload files always requires the project and company ID field.
        // If the project ID or company ID is not defined then just pass an empty string.
        // NOTE: CoreAPI doesn't seem to allow an empty string any more; see SphereDropzone.
        projectId: context.projectId ?? "",
        companyId: context.companyId ?? "",
        memberId: context.userId ?? "",
        onUploadProgress,
      });

      onUploadComplete(uploadResult.downloadUrl, context);

      return true;
    } catch (error) {
      const handleError = isSilentError
        ? sentryCaptureError
        : handleErrorWithToast;
      handleError({
        id: `uploadFileWithChunks-${Date.now().toString()}`,
        title: "Error uploading file",
        error,
      });
      return false;
    }
  }

  async function uploadMultipleFiles({
    files,
    finalizer,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    context,
  }: UploadMultipleFilesParams): Promise<boolean> {
    onUploadStart();

    // Adjust the number of concurrent file and chunk uploads to best utilize the bandwidth.
    uploadManager.setMaxConcurrentUploadsAuto(files);
    const uploadT0 = performance.now();

    const responses: MultiFileUploadResponse[] = await Promise.all(
      files.map(
        (file) =>
          // checkMagicFileHeader() is called in CoreFileUploader.doUpload().
          new Promise<MultiFileUploadResponse>((resolve) => {
            uploadManager.startFileUpload({
              file,
              context,
              coreApiClient: faroCoreApiClient,
              finalizer,
              onUploadCompleted: (
                id: GUID,
                fileName: string,
                fileSize: number,
                fileType: string,
                downloadUrl: string,
                md5: string
              ) => {
                resolve({
                  id,
                  fileName,
                  fileSize,
                  fileType,
                  downloadUrl,
                  md5,
                });
              },
              onUploadFailed: (id: GUID, fileName: string, error: Error) => {
                resolve({
                  id,
                  fileName,
                  errorMessage: error.message,
                });
              },
              onUploadCanceled: (id: GUID, fileName: string) => {
                resolve({
                  id,
                  fileName,
                });
              },
              onUploadUpdated: (_, progress) => {
                onUploadProgress(progress);
              },
            });
          })
      )
    );

    const uploadT1 = performance.now();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const bytesInMB = 1024 * 1024;

    const uploadsTimeSecs =
      (uploadT1 - uploadT0) / DateTimeUtils.MILLISECONDS_IN_SECOND;
    const successUploads = responses.filter(isUploadedFile);
    const successUploadsSize = successUploads.reduce(
      (sum: number, file: UploadedFile) => sum + file.fileSize,
      0
    );
    const successUploadSpeedMBps =
      successUploadsSize / bytesInMB / uploadsTimeSecs;

    const uploadedResponse: MultiUploadedFileResponse = {
      successful: successUploads,
      failed: responses.filter(isUploadFailedFile),
      canceled: responses.filter(isCanceledUploadFile),
      uploadSizeMB: Number((successUploadsSize / bytesInMB).toFixed(2)),
      uploadTimeSecs: Number(uploadsTimeSecs.toFixed(2)),
      uploadSpeedMBps: Number(successUploadSpeedMBps.toFixed(2)),
      uploadSpeedBucketMBps: getUploadSpeedBucket(successUploadSpeedMBps),
    };

    onUploadComplete(uploadedResponse, context);
    return true;
  }

  /** Used to add failed task in store */
  function addFailedUploadTaskInStore({
    fileName,
    errorMessage,
    context,
  }: InvalidUploadToStore): void {
    const isoDate = new Date().toISOString();

    const fileUploadTask: FileUploadTask = {
      id: uniqueId(),
      fileName,
      createdAt: isoDate,
      status: BackgroundTaskState.failed,
      progress: 0,
      context,
      errorMessage,
    };
    dispatch(setOne(fileUploadTask));
  }

  function validateAndAddFailedTask({
    file,
    allowedExtensions,
    maxFileSize,
    context,
  }: ValidateFile): boolean {
    const validatedFile = validateFile({
      file,
      allowedExtensions,
      maxFileSize,
    });
    if (!validatedFile.isValid) {
      openToast({
        title: validatedFile.message ?? "Invalid file",
        message: validatedFile.description,
        variant: "warning",
      });

      addFailedUploadTaskInStore({
        fileName: file.name,
        context,
        errorMessage: validatedFile.message ?? "Failed to import file",
      });
      return false;
    }

    return true;
  }

  return {
    uploadSingleFile,
    uploadFileWithChunks,
    uploadMultipleFiles,
    validateAndAddFailedTask,
  };
}
